import React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';

export type AlertVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark';

/*
 * Metrics and solid palettes mirror Dashkit's final .alert/.alert-* rules in
 * src/vendor/dashkit/css/theme.bundle.css, including its 1.5rem bottom margin.
 */
const alertVariants = cva(
  'tw:relative tw:mb-6 tw:rounded-md tw:border tw:border-transparent tw:px-5 tw:py-3',
  {
    variants: {
      variant: {
        primary: 'tw:border-primary tw:bg-primary tw:text-primary-foreground',
        secondary:
          'tw:border-secondary tw:bg-secondary tw:text-secondary-foreground',
        success: 'tw:border-success tw:bg-success tw:text-success-foreground',
        danger:
          'tw:border-destructive tw:bg-destructive tw:text-destructive-foreground',
        warning: 'tw:border-warning tw:bg-warning tw:text-warning-foreground',
        info: 'tw:border-info tw:bg-info tw:text-info-foreground',
        light: 'tw:border-light tw:bg-light tw:text-light-foreground',
        dark: 'tw:border-dark tw:bg-dark tw:text-white',
      },
    },
    defaultVariants: {
      variant: 'primary',
    },
  },
);

export type AlertProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onClose'
> & {
  variant?: AlertVariant;
  show?: boolean;
  onClose?: () => void;
  dismissible?: boolean;
};

const AlertRoot = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      className,
      variant,
      role = 'alert',
      show = true,
      dismissible,
      onClose,
      children,
      ...props
    },
    ref,
  ) => {
    if (!show) {
      return null;
    }

    return (
      <div
        ref={ref}
        role={role}
        className={cn(
          alertVariants({ variant }),
          // .alert-dismissible reserves 3.75rem for the close button.
          dismissible && 'tw:pr-15',
          className,
        )}
        {...props}>
        {dismissible && (
          <button
            type="button"
            /*
             * Mirrors .alert-dismissible .btn-close in
             * src/vendor/dashkit/css/theme.bundle.css. The icon inherits the
             * alert's text colour instead of Bootstrap's per-variant
             * background-image overrides.
             */
            className="tw:absolute tw:top-0 tw:right-0 tw:z-[2] tw:box-content tw:h-[0.5em] tw:w-[0.5em] tw:cursor-pointer tw:rounded-md tw:border-0 tw:bg-transparent tw:px-5 tw:py-[calc(0.75rem+0.5em)] tw:text-inherit tw:opacity-50 tw:hover:opacity-75"
            aria-label="Close"
            onClick={onClose}>
            <svg
              viewBox="0 0 16 16"
              aria-hidden="true"
              focusable="false"
              className="tw:block tw:h-full tw:w-full tw:fill-current">
              <path d="M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z" />
            </svg>
          </button>
        )}
        {children}
      </div>
    );
  },
);
AlertRoot.displayName = 'Alert';

const AlertHeading = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'tw:mt-0 tw:mb-[0.5625rem] tw:text-[0.9375rem] tw:font-medium tw:leading-[1.1] tw:text-inherit',
      className,
    )}
    {...props}>
    {children}
  </div>
));
AlertHeading.displayName = 'AlertHeading';

const Alert = Object.assign(AlertRoot, { Heading: AlertHeading });

export { Alert, AlertHeading, alertVariants };
